import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Typography
} from '@material-ui/core'
import clsx from 'clsx'
import { Form, Formik } from 'formik'
import { head } from 'lodash'
import { List, Paperclip, Trash2, Workflow } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import api from '../../services/api'
import { getBackendURL } from '../../services/config'
import { i18n } from '../../translate/i18n'
import { ButtonPrimary } from '../ButtonPrimary'
import { ButtonSecondary } from '../ButtonSecondary'
import ConfirmationModal from '../ConfirmationModal'
import { InputField } from '../InputField'
import { InputSwitch } from '../InputSwitch'
import { UsersSelect } from '../UsersSelect'
import { useStyles } from './styles'

const OptionSchema = Yup.object().shape({
  title: Yup.string().required('Obrigatório')
})

const DepartmentOptionsModal = ({
  open,
  onClose,
  option,
  onSave,
  onDeleteOption,
  onFlowUpdate
}) => {
  const classes = useStyles()
  const [optionAttachment, setOptionAttachment] = useState(null)
  const optionAttachmentFile = useRef(null)
  const [confirmationOpenOption, setConfirmationOpenOption] = useState(false)
  const [confirmationOpenDepartment, setConfirmationOpenDepartment] =
    useState(false)
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('')
  const [selectedOption, setSelectedOption] = useState(option)
  // const [selectOption, setSelectOption] = useState(false)
  const [queueUsers, setQueueUsers] = useState([])
  const [, setLoadingQueueUsers] = useState(false)
  const [mediaDeleted, setMediaDeleted] = useState(false)
  const [usersChanged, setUsersChanged] = useState(false)
  const [forwardedQueue, setForwardedQueue] = useState(null)

  const [imagePreviewOpen, setImagePreviewOpen] = useState(false)

  useEffect(() => {
    const fetchForwardedQueue = async () => {
      if (option?.forwardQueueId) {
        try {
          const { data } = await api.get(`/queue/${option.forwardQueueId}`)
          setForwardedQueue(data)
        } catch (error) {
          console.error(
            'Erro ao buscar dados do departamento encaminhado:',
            error
          )
          setForwardedQueue(null)
        }
      } else {
        setForwardedQueue(null)
      }
    }

    fetchForwardedQueue()
  }, [option])

  useEffect(() => {
    setSelectedOption(option)
    setMediaDeleted(false)
    setUsersChanged(false)
    if (option) {
      setOptionAttachment(null)
      // Se é um departamento (tem forwardQueueId), buscar usuários vinculados à queue
      if (option.forwardQueueId) {
        fetchQueueUsers(option.forwardQueueId)
      } else {
        setQueueUsers([])
      }
    }
  }, [option])

  const fetchQueueUsers = async queueId => {
    if (!queueId) return

    setLoadingQueueUsers(true)
    try {
      const { data } = await api.get(`/queue/${queueId}`)
      setQueueUsers(data.users || [])
    } catch (error) {
      console.error('Erro ao buscar usuários da queue:', error)
      setQueueUsers([])
    } finally {
      setLoadingQueueUsers(false)
    }
  }

  const handleOptionChange = (field, value) => {
    setSelectedOption(prev => ({ ...prev, [field]: value }))
    if (field === 'users') {
      setUsersChanged(true)
    }
  }

  const handleOptionAttachmentFile = e => {
    const file = head(e.target.files)
    if (file) {
      setOptionAttachment(file)
      setSelectedOption(prev => ({
        ...prev,
        mediaPath: URL.createObjectURL(file),
        mediaName: file.name
        // attachmentFile: file
      }))
    }
  }

  const deleteOptionMedia = async () => {
    if (optionAttachment) {
      setOptionAttachment(null)
      if (optionAttachmentFile.current) {
        optionAttachmentFile.current.value = null
      }
      handleOptionChange('mediaPath', null)
      handleOptionChange('mediaName', null)
      setConfirmationOpenOption(false)
      toast.success('Anexo removido com sucesso!')
      return
    }

    if (selectedOption?.mediaPath) {
      // Marca que o anexo deve ser deletado ao salvar
      setMediaDeleted(true)

      // Limpa a visualização/dados localmente
      handleOptionChange('mediaPath', null)
      handleOptionChange('mediaName', null)

      // Se fosse apenas um anexo do optionAttachment (novo), o toast já foi dado.
      // Damos um toast diferente para o anexo existente
      if (!optionAttachment) {
        toast.success(
          'Anexo marcado para exclusão. Clique em Salvar para confirmar.'
        )
      }
    }
    setConfirmationOpenOption(false)
  }

  const handleDelete = () => {
    setConfirmationOpenDepartment(true)
  }

  const handleConfirmDeleteDepartment = () => {
    if (deleteConfirmationText === 'EXCLUIR') {
      onDeleteOption(option)
      onClose()
      setConfirmationOpenDepartment(false)
      setDeleteConfirmationText('')
    } else {
      toast.error(
        'O texto de confirmação está incorreto. Digite EXCLUIR em letras maiúsculas.'
      )
    }
  }

  const handleSave = async values => {
    const optionToSave = { ...values }

    if (optionToSave.exitChatbot) {
      optionToSave.forwardQueueId = null
    }

    if (optionAttachment) {
      optionToSave.attachmentFile = optionAttachment
    }

    if (mediaDeleted && option?.id) {
      try {
        await api.delete(`/queue-options/${option.id}/media-upload`)
        optionToSave.mediaPath = null
        optionToSave.mediaName = null
      } catch (error) {
        console.error('Erro ao excluir anexo durante o salvamento:', error)
        toast.error('Erro ao excluir anexo. A opção não será salva.')
        return
      }
    }
    // Adicionar usuários selecionados ao subdepartamento
    if (selectedOption?.users) {
      optionToSave.users = selectedOption.users
    }

    let savedOption = null
    try {
      savedOption = await onSave(optionToSave)

      const finalOption = savedOption || option
      // const isNewOption = option?.id

      if (!values.menuOption && usersChanged && selectedOption?.users) {
        await handleUserBinding(finalOption)
      }

      if (onFlowUpdate) {
        await onFlowUpdate()
      }
      onClose()
    } catch (error) {
      console.error('Erro ao salvar subdepartamento:', error)
    }
  }

  const handleUserBinding = async option => {
    try {
      const userIds = (selectedOption.users || []).map(user => user.id)
      // Determinar o ID do departamento/subdepartamento baseado no tipo
      let targetId = null

      if (option?.id) {
        // Se estamos editando uma opção existente
        if (option.forwardQueueId) {
          // É um departamento (nível 2) - usar forwardQueueId
          targetId = option.forwardQueueId
        } else {
          // É uma opção simples - usar o próprio ID
          targetId = option.id
        }
      }

      if (!targetId) {
        console.warn(
          'Não foi possível determinar o ID do departamento/subdepartamento para vinculação'
        )
        return
      }

      // Verificar se é um departamento (queue) ou opção (queue-option)
      const isQueue = !!option?.forwardQueueId

      // Para cada usuário selecionado, vincular ao departamento/subdepartamento
      for (const userId of userIds) {
        try {
          // Buscar dados atuais do usuário
          const { data: userData } = await api.get(`/users/${userId}`)

          if (isQueue) {
            // Vinculação a departamento (queue)
            const currentQueueIds = userData.queues
              ? userData.queues.map(q => q.id)
              : []

            // Adicionar o departamento atual se não estiver já vinculado
            const updatedQueueIds = currentQueueIds.includes(targetId)
              ? currentQueueIds
              : [...currentQueueIds, targetId]

            // Atualizar o usuário com os novos queueIds
            await api.put(`/users/${userId}`, {
              queueIds: updatedQueueIds
            })
          } else {
            // Vinculação a opção de departamento (queue-option)
            const currentOptionIds = userData.queueOptions
              ? userData.queueOptions.map(opt => opt.id)
              : []

            // Adicionar a opção atual se não estiver já vinculada
            const updatedOptionIds = currentOptionIds.includes(targetId)
              ? currentOptionIds
              : [...currentOptionIds, targetId]

            // Atualizar o usuário com os novos queueOptionIds
            await api.put(`/users/${userId}`, {
              queueOptionIds: updatedOptionIds
            })
          }
        } catch (userError) {
          console.error(`Erro ao vincular usuário ${userId}:`, userError)
        }
      }

      // Se estamos editando, remover vinculações dos usuários que não estão mais selecionados
      const previousUsers = queueUsers || []
      // const currentSelectedUsers = selectedOption.users || []
      if (previousUsers.length > 0 || selectedOption.users.length > 0) {
        // Encontrar usuários que foram removidos
        const removedUsers = previousUsers.filter(
          prevUser =>
            !selectedOption.users.some(selUser => selUser.id === prevUser.id)
        )

        // Para cada usuário removido, remover a vinculação
        for (const removedUser of removedUsers) {
          try {
            const { data: userData } = await api.get(`/users/${removedUser.id}`)

            if (isQueue) {
              // Remover da lista de queues
              const currentQueueIds = userData.queues
                ? userData.queues.map(q => q.id)
                : []
              const updatedQueueIds = currentQueueIds.filter(
                qId => qId !== targetId
              )

              await api.put(`/users/${removedUser.id}`, {
                queueIds: updatedQueueIds
              })
            } else {
              // Remover da lista de queueOptions
              const currentOptionIds = userData.queueOptions
                ? userData.queueOptions.map(opt => opt.id)
                : []
              const updatedOptionIds = currentOptionIds.filter(
                optId => optId !== targetId
              )

              await api.put(`/users/${removedUser.id}`, {
                queueOptionIds: updatedOptionIds
              })
            }
          } catch (userError) {
            console.error(
              `Erro ao desvincular usuário ${removedUser.id}:`,
              userError
            )
          }
        }
      }
    } catch (error) {
      console.error('Erro ao vincular usuários:', error)
      toast.error('Erro ao vincular usuários ao departamento')
    }
  }

  const getMediaUrl = () => {
    if (optionAttachment) {
      return URL.createObjectURL(optionAttachment)
    }
    if (selectedOption?.mediaPath) {
      if (selectedOption.mediaPath.startsWith('http')) {
        return selectedOption.mediaPath
      }
      return `${getBackendURL()}/public/${selectedOption.mediaPath}`
    }
    return null
  }

  const isEditing = !!option?.id

  const ImagePreviewModalContent = (
    <Dialog
      maxWidth="xs"
      fullWidth={true}
      open={imagePreviewOpen}
      onClose={() => setImagePreviewOpen(false)}
    >
      <DialogContent className={classes.dialogContentPreview}>
        <img
          src={getMediaUrl()}
          alt="Preview em Tela Cheia"
          className={classes.imagePreview}
        />
      </DialogContent>
      <DialogActions>
        <ButtonSecondary
          onClick={() => setImagePreviewOpen(false)}
          label="Fechar"
        />
      </DialogActions>
    </Dialog>
  )

  return (
    <>
      <Dialog
        maxWidth="md"
        fullWidth={true}
        open={open}
        onClose={onClose}
        scroll="paper"
      >
        <Paper>
          <Formik
            initialValues={
              option
                ? {
                    ...option,
                    notificationact:
                      forwardedQueue?.notificationact === true ? true : false,
                    menuOption: option.menuOption ?? !option.forwardQueueId,
                    menuDepartment:
                      option.menuDepartment ?? !!option.forwardQueueId
                  }
                : {
                    title: '',
                    greetingMessage: '',
                    forwardQueueId: null,
                    exitChatbot: false,
                    mediaPath: null,
                    mediaName: null,
                    menuOption: true,
                    menuDepartment: false,
                    notificationact: false
                  }
            }
            enableReinitialize={true}
            validationSchema={OptionSchema}
            onSubmit={(values, actions) => {
              handleSave(values)
              actions.setSubmitting(false)
            }}
          >
            {({
              touched,
              errors,
              values,
              isSubmitting,
              setFieldValue,
              handleChange
            }) => {
              let specificType = null

              if (values.menuOption) {
                specificType = (
                  <div className={classes.iconWrapper}>
                    <span className={classes.iconWrapperText}>
                      <strong>Modo Ativo: </strong>
                      <List className={classes.icon} />
                      Menu de Opções
                    </span>
                  </div>
                )
              } else if (values.menuDepartment) {
                specificType = (
                  <div className={classes.iconWrapper}>
                    <span className={classes.iconWrapperText}>
                      <strong>Modo Ativo: </strong>
                      <Workflow className={classes.icon} />
                      Departamento
                    </span>
                  </div>
                )
              }

              const handleToggleSwitch = (field, checked) => {
                if (field === 'menuOption') {
                  if (checked) {
                    setFieldValue('menuOption', true)
                    setFieldValue('menuDepartment', false)
                  } else {
                    setFieldValue('menuOption', false)
                    setFieldValue('menuDepartment', true)
                  }
                } else if (field === 'menuDepartment') {
                  if (checked) {
                    setFieldValue('menuDepartment', true)
                    setFieldValue('menuOption', false)
                  } else {
                    setFieldValue('menuDepartment', false)
                    setFieldValue('menuOption', true)
                  }
                }
              }
              return (
                <>
                  <DialogTitle
                    className={clsx(classes.dialogTitle, {
                      [classes.dialogTitleActiveOption]: values.menuOption,
                      [classes.dialogTitleActiveDepartment]:
                        values.menuDepartment
                    })}
                  >
                    {specificType}
                  </DialogTitle>

                  <Form>
                    <DialogContent dividers className={classes.dialogContent}>
                      <ConfirmationModal
                        title="Deletar Anexo do Setor ou Subsetor"
                        open={confirmationOpenOption}
                        onClose={() => setConfirmationOpenOption(false)}
                        onConfirm={deleteOptionMedia}
                      >
                        Tem certeza de que deseja excluir o anexo? Esta ação não
                        pode ser desfeita.
                      </ConfirmationModal>

                      <ConfirmationModal
                        title="Excluir Departamento"
                        open={confirmationOpenDepartment}
                        onClose={() => {
                          setConfirmationOpenDepartment(false)
                          setDeleteConfirmationText('')
                        }}
                        onConfirm={handleConfirmDeleteDepartment}
                      >
                        <div>
                          <Typography variant="body1">
                            Para confirmar a exclusão do departamento, digite
                            "EXCLUIR" no campo abaixo:
                          </Typography>
                          <InputField
                            value={deleteConfirmationText}
                            onChange={e =>
                              setDeleteConfirmationText(
                                e.target.value.toUpperCase()
                              )
                            }
                            label="Confirme a exclusão"
                            fullWidth
                            autoFocus
                          />
                        </div>
                      </ConfirmationModal>

                      <Grid
                        container
                        spacing={2}
                        className={classes.boxContainer}
                      >
                        <Grid item xs={8} className={classes.boxForm}>
                          <Grid item xs={12} className={classes.boxInput}>
                            <Grid
                              item
                              xs={12}
                              className={classes.boxInputWrapper}
                            >
                              <Grid item xs={8}>
                                <InputField
                                  value={values.title || ''}
                                  spellCheck={true}
                                  onChange={e =>
                                    setFieldValue('title', e.target.value)
                                  }
                                  size="small"
                                  className={classes.input}
                                  label="Nome"
                                  name="title"
                                  id="title"
                                  placeholder="Digite o nome"
                                  error={touched.title && Boolean(errors.title)}
                                  helperText={touched.title && errors.title}
                                  emoji
                                />
                              </Grid>
                              <Grid item xs={4} className={classes.inputSwitch}>
                                {!values.menuOption && (
                                  <InputSwitch
                                    label="Aguardar Atendente"
                                    checked={values.exitChatbot || false}
                                    disabled={
                                      values.forwardQueueId &&
                                      !values.exitChatbot
                                    }
                                    onChange={e =>
                                      setFieldValue(
                                        'exitChatbot',
                                        e.target.checked
                                      )
                                    }
                                    name="exitChatbot"
                                    size="small"
                                  />
                                )}
                              </Grid>
                            </Grid>

                            {!values.menuOption && (
                              <>
                                <Grid item xs={12}>
                                  <InputField
                                    multiline
                                    value={values.greetingMessage || ''}
                                    spellCheck={true}
                                    onChange={e =>
                                      setFieldValue(
                                        'greetingMessage',
                                        e.target.value
                                      )
                                    }
                                    size="small"
                                    className={classes.input}
                                    placeholder="Olá! Seja bem-vindo(a)..."
                                    label="Mensagem de Saudação"
                                    name="greetingMessage"
                                    id="greetingMessage"
                                    emoji
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <InputSwitch
                                    label="Não receber notificações para este departamento"
                                    checked={values.notificationact || false}
                                    onChange={e =>
                                      setFieldValue(
                                        'notificationact',
                                        e.target.checked
                                      )
                                    }
                                    name="notificationact"
                                    size="small"
                                  />{' '}
                                </Grid>
                                <Grid item xs={12}>
                                  <UsersSelect
                                    label="Usuários do Departamento de Destino"
                                    initialUsers={queueUsers || []}
                                    onFiltered={users =>
                                      handleOptionChange('users', users)
                                    }
                                  />
                                </Grid>

                                <Grid
                                  item
                                  xs={12}
                                  className={classes.attachmentContainer}
                                >
                                  <Grid
                                    item
                                    xs={3}
                                    className={classes.buttonsAttachmentFile}
                                  >
                                    {selectedOption?.mediaPath ||
                                    optionAttachment ? (
                                      <div
                                        style={{
                                          textAlign: 'center',
                                          cursor: 'pointer'
                                        }}
                                        onClick={() =>
                                          setImagePreviewOpen(true)
                                        }
                                      >
                                        <img
                                          src={getMediaUrl()}
                                          alt="Anexo Preview"
                                          className={
                                            classes.attachmentPreviewImage
                                          }
                                          onError={e => {
                                            e.target.style.display = 'none'
                                          }}
                                        />
                                      </div>
                                    ) : (
                                      <Typography
                                        variant="body1"
                                        color="textSecondary"
                                        className={classes.attachmentNotFile}
                                      >
                                        <Paperclip />
                                        Sem anexo
                                      </Typography>
                                    )}
                                  </Grid>

                                  <Grid
                                    item
                                    xs={4}
                                    className={classes.buttonsAttachment}
                                  >
                                    <div
                                      className={classes.buttonsBoxAttachment}
                                    >
                                      <div style={{ display: 'none' }}>
                                        <input
                                          type="file"
                                          ref={optionAttachmentFile}
                                          onChange={handleOptionAttachmentFile}
                                          accept=".jpg,.jpeg,.png,.bmp,image/jpeg,image/png,image/bmp"
                                        />
                                      </div>
                                      <Button
                                        onClick={() =>
                                          optionAttachmentFile.current.click()
                                        }
                                        className={classes.buttonFile}
                                      >
                                        <Paperclip />
                                        <span>
                                          {optionAttachment ||
                                          selectedOption?.mediaPath
                                            ? 'Anexar'
                                            : 'Anexar'}
                                        </span>
                                      </Button>
                                      {(optionAttachment ||
                                        selectedOption?.mediaPath) && (
                                        <IconButton
                                          onClick={e => {
                                            e.stopPropagation()
                                            setConfirmationOpenOption(true)
                                          }}
                                          aria-label="delete"
                                          className={classes.iconButtonDelete}
                                        >
                                          <Trash2 />
                                        </IconButton>
                                      )}
                                    </div>
                                    <Grid item xs={12}>
                                      <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        style={{
                                          marginTop: '8px',
                                          display: 'block'
                                        }}
                                      >
                                        Formatos: JPG, JPEG, PNG, BMP
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </>
                            )}
                          </Grid>

                          <Grid item xs={12} className={classes.boxContentHelp}>
                            <Grid item xs={12} className={classes.boxInfoHelp}>
                              <strong>Boas Práticas</strong>
                              <ul className={classes.helpList}>
                                <li>Nome claro e curto (Ex: "Suporte N1").</li>
                                <li>
                                  Saudação Objetiva (diga o que acontece a
                                  seguir).
                                </li>
                                <li>
                                  Habilite "Aguardar Atendente para desativar" o
                                  "#" início.
                                </li>
                              </ul>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={4}>
                          <div className={classes.boxHelp}>
                            <div
                              className={`${classes.boxHelpContainer} ${
                                values.menuOption
                                  ? classes.boxHelpContainerActiveOption
                                  : ''
                              }`}
                            >
                              <div className={classes.helpTitle}>
                                <div className={classes.switchContainer}>
                                  {values.menuOption && (
                                    <span className={classes.activeBadge}>
                                      ativo
                                    </span>
                                  )}
                                  <InputSwitch
                                    checked={values.menuOption || false}
                                    onChange={e =>
                                      handleToggleSwitch(
                                        'menuOption',
                                        e.target.checked
                                      )
                                    }
                                    name="menuOption"
                                    size="small"
                                    disabled={isEditing}
                                    classes={{
                                      root: classes.customSwitchRoot,
                                      checked: classes.checked,
                                      disabled: classes.disabled
                                    }}
                                  />
                                </div>
                                <div
                                  className={clsx(classes.helpTitleText, {
                                    [classes.dialogTitleActiveOption]:
                                      values.menuOption
                                  })}
                                >
                                  <List />
                                  Menu de Opções
                                </div>
                              </div>
                              <Typography
                                variant="body1"
                                className={classes.helpText}
                              >
                                Crie escolhas para o cliente, como um mini menu
                                dentro do fluxo. Por Exemplo:
                                <p>
                                  <span className={classes.badge}>1</span>{' '}
                                  Vendas
                                </p>
                                <p>
                                  <span className={classes.badge}>2</span>{' '}
                                  Suporte
                                </p>
                                Ao digitar
                                <span className={classes.badge}>1</span>, o bot
                                vai mostrar as próximas opções, como:
                                <p>
                                  <span className={classes.badge}>1</span> Venda
                                  Local
                                </p>
                                <p>
                                  <span className={classes.badge}>2</span> Venda
                                  Online
                                </p>
                              </Typography>
                            </div>

                            <div
                              className={`${classes.boxHelpContainer} ${
                                values.menuDepartment
                                  ? classes.boxHelpContainerActiveDepartment
                                  : ''
                              }`}
                            >
                              <div className={classes.helpTitle}>
                                <div className={classes.switchContainer}>
                                  {values.menuDepartment && (
                                    <span className={classes.activeBadge}>
                                      ativo
                                    </span>
                                  )}
                                  <InputSwitch
                                    checked={values.menuDepartment || false}
                                    onChange={e =>
                                      handleToggleSwitch(
                                        'menuDepartment',
                                        e.target.checked
                                      )
                                    }
                                    name="menuDepartment"
                                    size="small"
                                    disabled={isEditing}
                                    classes={{
                                      root: classes.customSwitchRoot,
                                      checked: classes.checked,
                                      disabled: classes.disabled
                                    }}
                                  />
                                </div>

                                <div
                                  className={clsx(classes.helpTitleText, {
                                    [classes.dialogTitleActiveDepartment]:
                                      values.menuDepartment
                                  })}
                                >
                                  <Workflow />
                                  Departamento
                                </div>
                              </div>
                              <Typography
                                variant="body1"
                                className={classes.helpText}
                              >
                                Fase final do fluxo, é onde os atendentes
                                escolhidos ficarão disponíveis para receber os
                                clientes. Nesta etapa é possível adicionar:
                                <ul className={classes.helpList}>
                                  <li>Mensagem de boas-vindas</li>
                                  <li>Anexar arquivos</li>
                                  <li>Definir quem vai atender.</li>
                                </ul>
                              </Typography>
                            </div>
                          </div>
                        </Grid>
                      </Grid>
                    </DialogContent>

                    <DialogActions className={classes.dialogActions}>
                      <div className={classes.buttonDeleteOption}>
                        {selectedOption?.id && (
                          <IconButton
                            onClick={handleDelete}
                            disabled={isSubmitting}
                            className={classes.deleteButton}
                          >
                            <Trash2 />
                          </IconButton>
                        )}
                      </div>

                      <div className={classes.buttonsActions}>
                        <ButtonSecondary
                          onClick={onClose}
                          disabled={isSubmitting}
                          label="Cancelar"
                        />
                        <ButtonPrimary
                          type="submit"
                          label={
                            selectedOption?.id
                              ? `${i18n.t('queueModal.buttons.okEdit')}`
                              : `${i18n.t('queueModal.buttons.okAdd')}`
                          }
                          disabled={isSubmitting}
                        />
                      </div>
                    </DialogActions>
                  </Form>
                </>
              )
            }}
          </Formik>
        </Paper>
      </Dialog>
      {ImagePreviewModalContent}
    </>
  )
}

export default DepartmentOptionsModal
